import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Drama } from "@/mocks/dramas";

interface HeroCarouselProps {
  items: Drama[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const count = items.length;

  const go = useCallback(
    (next: number) => setIndex((next + count) % count),
    [count]
  );

  useEffect(() => {
    if (count <= 1) return;
    const timer = setInterval(() => setIndex((i) => (i + 1) % count), 5500);
    return () => clearInterval(timer);
  }, [count]);

  if (count === 0) return null;
  const current = items[index];

  return (
    <section className="relative w-full" aria-label="焦点剧集轮播">
      <div className="relative h-[440px] md:h-[620px] w-full overflow-hidden">
        {items.map((item, i) => (
          <div
            key={item.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === index ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
            aria-hidden={i !== index}
          >
            <img
              src={item.backdrop}
              alt={`${item.title} 剧照`}
              title={`${item.title} 焦点剧集`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-secondary-950/85 via-secondary-950/55 to-secondary-950/20"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-secondary-950/70 via-transparent to-transparent"></div>
          </div>
        ))}

        <div className="relative z-10 flex h-full w-full items-center px-4 md:px-6">
          <div className="max-w-2xl animate-fade-up" key={current.id}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-md bg-accent-500 px-2.5 py-1 font-label text-xs font-bold text-primary-950">
                焦点剧集
              </span>
              {current.types.map((t) => (
                <span
                  key={t}
                  className="rounded-md border border-background-50/30 bg-background-50/10 px-2.5 py-1 text-xs text-background-50 backdrop-blur"
                >
                  {t}
                </span>
              ))}
            </div>
            <h2 className="mt-4 font-heading text-3xl md:text-5xl font-black text-background-50 drop-shadow">
              {current.title}
            </h2>
            <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-background-100/90">
              <span>导演 {current.director}</span>
              <span className="text-background-100/40">|</span>
              <span>{current.region}</span>
              <span className="text-background-100/40">|</span>
              <span>{current.year} 首播</span>
              <span className="text-background-100/40">|</span>
              <span>全 {current.episodes} 集</span>
            </p>
            <p className="mt-4 max-w-xl text-sm md:text-base leading-relaxed text-background-100/85 line-clamp-3">
              {current.synopsis}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${current.id}`}
                className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
              >
                <i className="ri-play-circle-line text-lg"></i>
                查看剧集详情
              </Link>
              <Link
                to={`/category?type=${encodeURIComponent(current.types[0])}`}
                className="inline-flex items-center gap-2 rounded-md border border-background-50/40 bg-background-50/10 px-5 py-2.5 text-sm font-medium text-background-50 whitespace-nowrap cursor-pointer backdrop-blur hover:bg-background-50/20 transition-colors"
              >
                <i className="ri-list-check-2"></i>
                更多同类剧集
              </Link>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => go(index - 1)}
          aria-label="上一张"
          className="absolute left-3 md:left-6 top-1/2 z-20 flex h-9 w-9 md:h-11 md:w-11 -translate-y-1/2 items-center justify-center rounded-full border border-background-50/30 bg-secondary-950/40 text-background-50 backdrop-blur cursor-pointer hover:bg-primary-500 transition-colors"
        >
          <i className="ri-arrow-left-s-line text-xl"></i>
        </button>
        <button
          type="button"
          onClick={() => go(index + 1)}
          aria-label="下一张"
          className="absolute right-3 md:right-6 top-1/2 z-20 flex h-9 w-9 md:h-11 md:w-11 -translate-y-1/2 items-center justify-center rounded-full border border-background-50/30 bg-secondary-950/40 text-background-50 backdrop-blur cursor-pointer hover:bg-primary-500 transition-colors"
        >
          <i className="ri-arrow-right-s-line text-xl"></i>
        </button>

        <div className="absolute bottom-5 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2">
          {items.map((item, i) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={`切换到 ${item.title}`}
              className={`h-1.5 rounded-full transition-all cursor-pointer ${
                i === index ? "w-7 bg-accent-500" : "w-2.5 bg-background-50/50 hover:bg-background-50/80"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}